import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";

export class GeotabApiError extends Error {
  constructor(message, { code = "", status = 0 } = {}) {
    super(message);
    this.name = "GeotabApiError";
    this.code = code;
    this.status = status;
  }
}

async function getJson(url, options) {
  const response = await fetchWithSupabaseAuth(url, options);
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = payload?.detail;
    const message = typeof detail === "string" ? detail : detail?.message || "Geotab est indisponible.";
    throw new GeotabApiError(message, { code: detail?.code || "", status: response.status });
  }
  return payload;
}

export const fetchGeotabCalendar = (path, options) => getJson(`/api/geotab${path}`, options);

export const fetchGeotabStatus = () => getJson("/api/geotab/status");
const thresholdQuery = (t = {}) =>
  `&radius_m=${t.radius_m ?? 1000}&tolerance_min=${t.tolerance_min ?? 15}&min_off_min=${t.min_off_min ?? 10}`;
const periodQuery = (period) => `period=${period === "evening" ? "evening" : "day"}`;

// plan : « closure », heures du plan clôturé ; « current », ce plan rejoué avec les réglages actuels.
export const fetchGeotabComparison = (date, period = "day", thresholds, plan = "current") =>
  getJson(`/api/geotab/comparison?date=${encodeURIComponent(date)}&${periodQuery(period)}&plan=${plan === "closure" ? "closure" : "current"}${thresholdQuery(thresholds)}`);
// Réel du Gantt du Dispatch (droit « Réel Geotab dans le Dispatch ») : revision, version du Dispatch affiché ;
// le serveur partage une même lecture pendant 60 s.
// Avis en direct de la journée en cours (même droit) : une lecture partagée par tous pendant 45 s.
export const fetchDispatchGeotabLiveAlerts = () => getJson("/api/dispatch/geotab-live-alerts");
// Liste Geotab d'une journée en cours ou passée (même droit) : écarts constatés et, pour le passé, Comparatif des commandes.
export const fetchDispatchGeotabCheck = (date, thresholds, revision = "") =>
  getJson(`/api/dispatch/geotab-check?date=${encodeURIComponent(date)}${thresholdQuery(thresholds)}&revision=${encodeURIComponent(revision)}`);
// Tracé GPS daté d'un camion signalé, sur la même lecture partagée que le contrôle (mêmes seuils, même version).
export const fetchDispatchGeotabCheckTrace = (date, truckId, thresholds, revision = "", options) =>
  getJson(`/api/dispatch/geotab-check/trace?date=${encodeURIComponent(date)}&truck_id=${encodeURIComponent(truckId)}${thresholdQuery(thresholds)}&revision=${encodeURIComponent(revision)}`, options);
// « C'est le bon lieu » : l'arrêt [lat, lon] devient la position confirmée des fiches à l'adresse de la commande ; null la retire.
export const saveDispatchGeotabConfirmedPosition = (orderId, stop) => putJson("/api/dispatch/geotab-check/confirmed-position",
  { order_id: orderId, latitude: stop ? stop[0] : null, longitude: stop ? stop[1] : null }, "Position non enregistrée.");
export const fetchDispatchGeotabReal = (date, period = "day", thresholds, revision = "") =>
  getJson(`/api/dispatch/geotab-real?date=${encodeURIComponent(date)}&${periodQuery(period)}${thresholdQuery(thresholds)}&revision=${encodeURIComponent(revision)}`);
// Rapports > Comparatif Geotab : plage indépendante du Dispatch, bornes incluses.
export const fetchGeotabOrdersReport = (dateFrom, period = "day", dateTo = dateFrom) =>
  getJson(`/api/reports/geotab-orders?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}&${periodQuery(period)}${thresholdQuery()}`);
// Consolidation : la journée réelle face au Dispatch tel qu'il a été remis (plan de clôture), pas rejoué aux réglages actuels.
export const fetchConsolidationGeotabComparison = (date, period = "day", options) =>
  getJson(`/api/billing/consolidation/geotab/comparison?date=${encodeURIComponent(date)}&${periodQuery(period)}&plan=closure${thresholdQuery()}`, options);
export const fetchConsolidationGeotabPreferences = () => getJson("/api/billing/consolidation/geotab/preferences");
export const fetchConsolidationRouteTrace = (date, { deviceId, fromMin, toMin }, options) =>
  getJson(`/api/billing/consolidation/geotab/trace?date=${encodeURIComponent(date)}&device_id=${encodeURIComponent(deviceId)}&from_min=${fromMin}&to_min=${toMin}`, options);
// Préchargements : arrêts Geotab de la veille au soir (evening) des camions préchargés et des employés choisis.
export const fetchConsolidationPreloadGeotab = (dates, period, evening, employees = []) =>
  getJson(`/api/billing/consolidation/geotab/preloads?${dates.map((date) => `date=${encodeURIComponent(date)}`).join("&")}&${periodQuery(period)}&evening=${encodeURIComponent(evening)}${employees.map((id) => `&employee=${encodeURIComponent(id)}`).join("")}${thresholdQuery()}`);
// Tableau de bord : journées clôturées seulement, sans le calcul des réglages du Calibrage.
export const fetchGeotabDashboard = (from, to, period = "day", thresholds) =>
  getJson(`/api/geotab/dashboard?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&${periodQuery(period)}${thresholdQuery(thresholds)}`);
export const fetchGeotabCalibration = (from, to, period = "day", thresholds) =>
  getJson(`/api/geotab/calibration?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&${periodQuery(period)}${thresholdQuery(thresholds)}`);
// chosen : valeur tapée à la place de la suggestion, que le Calibrage retient.
const changesBody = (changes) => JSON.stringify({ changes: changes.map(({ kind, id, field, suggested, updated_at: updatedAt, chosen }) => ({ kind, id, field, value: suggested, updated_at: updatedAt || null, chosen: Boolean(chosen) })) });
// Calibrage refait comme si les réglages essayés étaient appliqués : rien n'est écrit.
export async function simulateGeotabCalibration(from, to, period, thresholds, changes) {
  const response = await fetchWithSupabaseAuth(`/api/geotab/calibration/simulate?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&${periodQuery(period)}${thresholdQuery(thresholds)}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: changesBody(changes),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = payload?.detail;
    throw new GeotabApiError(typeof detail === "string" ? detail : detail?.message || "Simulation impossible.", { status: response.status });
  }
  return payload;
}
// Réglages du Calibrage écrits dans Paramètres système, Flotte et Clients et sites.
export async function applyGeotabCalibration(changes) {
  const response = await fetchWithSupabaseAuth("/api/geotab/calibration/apply", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: changesBody(changes),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = payload?.detail;
    const message = response.status === 409 ? "Une fiche a changé entre-temps. Cliquez sur Calculer, puis réessayez."
      : typeof detail === "string" ? detail : detail?.message || "Réglages non appliqués.";
    throw new GeotabApiError(message, { status: response.status });
  }
  return payload;
}
// Ne plus retenir des valeurs choisies (« kind|id|field ») : le Calibrage les repropose d'après la mesure.
export async function forgetGeotabCalibrationChoices(keys) {
  const response = await fetchWithSupabaseAuth("/api/geotab/calibration/choices/forget", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ keys }),
  });
  if (!response.ok) throw new GeotabApiError("Choix non retiré.", { status: response.status });
  return response.json();
}
export const fetchGeotabLive = () => getJson("/api/geotab/live");
export const fetchGeotabAssignment = (date, period, truckId) =>
  getJson(`/api/geotab/assignment?date=${encodeURIComponent(date)}&${periodQuery(period)}&truck_id=${encodeURIComponent(truckId)}`);
// Paramètres de la section, enregistrés pour l'utilisateur connecté.
export const fetchGeotabPreferences = () => getJson("/api/me/ui-preferences/geotab");
export async function saveGeotabPreferences(preferences) {
  const response = await fetchWithSupabaseAuth("/api/me/ui-preferences/geotab", {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(preferences),
  });
  if (!response.ok) throw new GeotabApiError("Paramètres Geotab non enregistrés.", { status: response.status });
}
export const fetchGeotabDevices = () => getJson("/api/geotab/devices");
export const fetchGeotabDeviceAssociations = () => getJson("/api/geotab/device-associations");
export const fetchGeotabDeviceDrivers = () => getJson("/api/geotab/device-drivers");
async function putJson(url, body, failure = "Association non enregistrée.") {
  const response = await fetchWithSupabaseAuth(url, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new GeotabApiError(payload?.detail?.message || payload?.detail || failure, { status: response.status });
  return payload;
}
// À vérifier : { name, decision } pour une adresse, { lat, lon, stop_class } pour un arrêt récurrent ; null remet la ligne à vérifier.
export const saveGeotabReviewDecision = (body) => putJson("/api/geotab/review-decisions", body, "Décision non enregistrée.");
// since : date d'effet (aaaa-mm-jj) ; les journées d'avant gardent l'association précédente.
export const saveGeotabDeviceOverride = (truckId, deviceId, since = null) =>
  putJson(`/api/geotab/device-overrides/${encodeURIComponent(truckId)}`, { device_id: deviceId || null, since: since || null });
export const saveGeotabDeviceReplacement = (deviceId, truckId, since = null) =>
  putJson(`/api/geotab/device-replacements/${encodeURIComponent(deviceId)}`, { truck_id: truckId || null, since: since || null });
const fetchGeotabTrace = (date, { deviceId, fromMin, toMin }) =>
  getJson(`/api/geotab/trace?date=${encodeURIComponent(date)}&device_id=${encodeURIComponent(deviceId)}`
    + `${fromMin != null ? `&from_min=${fromMin}` : ""}${toMin != null ? `&to_min=${toMin}` : ""}`);
// Trace réelle bout à bout quand le chauffeur a conduit plusieurs camions.
export const fetchGeotabTraces = (date, segments) =>
  Promise.all(segments.map((segment) => fetchGeotabTrace(date, segment))).then((parts) => parts.flatMap((part) => part.points || []));
